import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell } from 'lucide-react';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import BroadcastMessageBody from '@/components/BroadcastMessageBody';
import { useTranslation } from 'react-i18next';

const POLL_MS = 2 * 60 * 1000;

type BroadcastRow = {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  acknowledged: boolean;
};

function formatBroadcastTime(
  iso: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (iso == null || iso === '') return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 24) {
    return t('notifications.todayAt', {
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    });
  }
  if (diffHours < 48) {
    return t('notifications.yesterdayAt', {
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    });
  }
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface KioskNotificationBellProps {
  compact?: boolean;
}

type PanelLayout = { top: number; left: number; width: number; maxHeight: number };

function isUsableTriggerRect(rect: DOMRect, vw: number, vh: number): boolean {
  return (
    rect.width >= 2 &&
    rect.height >= 2 &&
    rect.bottom > 4 &&
    rect.top < vh &&
    rect.right > 4 &&
    rect.left < vw
  );
}

export default function KioskNotificationBell({ compact = false }: KioskNotificationBellProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<BroadcastRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [panelLayout, setPanelLayout] = useState<PanelLayout | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const computePanelLayout = useCallback((): PanelLayout | null => {
    const trigger = buttonRef.current ?? rootRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 12;
    const gap = 6;
    const width = Math.min(vw - margin * 2, 20 * 16);

    const banner = document.querySelector<HTMLElement>('[role="banner"]');
    const bannerRect = banner?.getBoundingClientRect();

    let top: number;
    let left: number;

    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      if (isUsableTriggerRect(rect, vw, vh)) {
        top = rect.bottom + gap;
        left = rect.right - width;
        left = Math.min(Math.max(margin, left), vw - width - margin);
      } else if (bannerRect && bannerRect.height > 0 && bannerRect.bottom > 0) {
        top = bannerRect.bottom + gap;
        left = vw - width - margin;
      } else {
        top = Math.min(margin + 72, vh * 0.15);
        left = vw - width - margin;
      }
    } else if (bannerRect && bannerRect.height > 0 && bannerRect.bottom > 0) {
      top = bannerRect.bottom + gap;
      left = vw - width - margin;
    } else {
      top = Math.min(margin + 72, vh * 0.15);
      left = vw - width - margin;
    }

    const maxHeight = Math.max(120, vh - top - margin);
    return { top, left, width, maxHeight };
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPanelLayout(null);
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 16;

    const tick = () => {
      if (cancelled) return;
      const trigger = buttonRef.current ?? rootRef.current;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const rect = trigger?.getBoundingClientRect();
      const canUseTrigger = rect != null && isUsableTriggerRect(rect, vw, vh);

      if (!canUseTrigger && attempts < maxAttempts) {
        attempts += 1;
        requestAnimationFrame(tick);
        return;
      }

      const layout = computePanelLayout();
      if (!cancelled && layout != null) {
        setPanelLayout(layout);
      }
    };

    tick();
    return () => {
      cancelled = true;
    };
  }, [open, items.length, loading, computePanelLayout]);

  useEffect(() => {
    if (!open) return;
    const onWin = () => {
      const next = computePanelLayout();
      if (next) setPanelLayout(next);
    };
    window.addEventListener('resize', onWin);
    window.addEventListener('orientationchange', onWin);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('orientationchange', onWin);
    };
  }, [open, computePanelLayout]);

  const load = useCallback(async () => {
    if (!apiService.getAccessToken()) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.getMyAdminBroadcasts();
      if (res.success && res.data?.broadcasts) {
        setItems(res.data.broadcasts);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onAuth = () => void load();
    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
    return () => window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const unreadCount = items.filter((b) => !b.acknowledged).length;

  const handleRead = useCallback(async (id: string, isRead: boolean) => {
    if (isRead) return;
    await apiService.acknowledgeAdminBroadcast(id);
    await load();
  }, [load]);

  const btnSize = compact ? 'h-8 w-8 min-h-8 min-w-8' : 'h-10 w-10 min-h-10 min-w-10';
  const iconSize = compact ? 'h-4 w-4' : 'h-[18px] w-[18px] sm:h-5 sm:w-5';

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        className={[
          `relative flex ${btnSize} items-center justify-center`,
          'rounded-[10px] border-2 bg-black/40 shadow-[0_0_12px_rgba(255,200,0,0.18)] backdrop-blur-md',
          'transition-colors duration-200 hover:bg-black/55',
        ].join(' ')}
        style={{ borderColor: 'rgba(255, 200, 0, 0.5)', color: '#fff' }}
        aria-expanded={open}
        aria-label={t('notifications.ariaLabel')}
      >
        <Bell className={iconSize} strokeWidth={2.2} color="#fff" />
        {unreadCount > 0 ? (
          <span
            className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full ring-2 ring-[#040F29]"
            style={{ background: '#ff3b30' }}
            aria-label="Unread notifications"
          />
        ) : null}
      </button>

      {open && panelLayout != null
        ? createPortal(
            <div
              ref={panelRef}
              className="overflow-hidden rounded-[14px] border border-white/10 bg-[#111328] text-white shadow-[0_0_20px_rgba(0,0,0,0.45)] backdrop-blur-md ring-1 ring-white/10"
              style={{
                position: 'fixed',
                top: panelLayout.top,
                left: panelLayout.left,
                width: Math.min(panelLayout.width, 320),
                maxHeight: panelLayout.maxHeight,
                zIndex: 1080,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
              role="dialog"
              aria-label={t('notifications.listAriaLabel')}
            >
              <div className="flex flex-shrink-0 items-center gap-2 border-b border-white/10 px-4 py-[14px]">
                <p className="text-[13px] font-medium text-[#e0e0f0]">{t('notifications.title')}</p>
                {unreadCount > 0 ? (
                  <span className="rounded-full bg-[#7c6df5]/15 px-[7px] py-[2px] text-[10px] font-semibold text-[#a89ef8]">
                    {t('notifications.newCount', { count: unreadCount })}
                  </span>
                ) : null}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {loading && items.length === 0 ? (
                  <div className="space-y-2 px-4 py-3">
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <div key={idx} className="rounded-md bg-white/5 px-3 py-2">
                        <div className="h-3 w-24 rounded bg-white/10" />
                        <div className="mt-2 h-2.5 w-full rounded bg-white/10" />
                        <div className="mt-1 h-2.5 w-2/3 rounded bg-white/10" />
                      </div>
                    ))}
                  </div>
                ) : items.length === 0 ? (
                  <p className="px-4 py-8 text-center text-[13px] text-[rgba(150,150,180,0.45)]">
                    {t('notifications.none')}
                  </p>
                ) : (
                  <ul>
                    {items.map((b) => {
                      const timeLabel = formatBroadcastTime(b.createdAt, t);
                      const isRead = Boolean(b.acknowledged);
                      return (
                        <li key={b.id}>
                          <div
                            role="button"
                            tabIndex={0}
                            className="flex w-full cursor-pointer items-start gap-[10px] border-b border-white/10 px-4 py-3 text-left transition last:border-b-0 hover:bg-white/5"
                            style={{ opacity: isRead ? 0.5 : 1 }}
                            onClick={() => void handleRead(b.id, isRead)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                void handleRead(b.id, isRead);
                              }
                            }}
                          >
                            <span
                              className="mt-[5px] h-[6px] w-[6px] flex-shrink-0 rounded-full"
                              style={{ background: isRead ? 'transparent' : '#7c6df5' }}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="mb-[3px] block text-[13px] font-medium text-[#e0e0f0]">
                                {b.title || 'Notification'}
                              </span>
                              <span className="mb-[6px] block whitespace-pre-wrap text-xs leading-[1.5] text-[rgba(180,180,210,0.6)]">
                              <BroadcastMessageBody
                                text={b.body}
                                linkClassName="break-all border-b border-[#7c6df5]/30 text-[#7c6df5] no-underline"
                                onLinkClick={(e) => e.stopPropagation()}
                              />
                              </span>
                            {timeLabel ? (
                                <span className="block text-[11px] text-[rgba(120,120,160,0.45)]">
                                  {timeLabel}
                                </span>
                            ) : null}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
